import { OptimizationRunStatus } from '@warebee/frontend/data-access-api-graphql';
import classNames from 'classnames';
import _ from 'lodash';
import { nanoid } from 'nanoid';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
// import html2canvas from 'html2canvas';
import AllocationPreview from '../allocation/AllocationPreview';
import AnalyzedProductsTable from '../analyze/AnalyzedProductsTable';
import SwitchAnalyzeType from '../analyze/SwitchAnalyzeType';
import AnalyzeDashboard from '../analyze/dashboard/AnalyzeDashboard';
import AssignmentPolicyProductByRuleTableView from '../assignmentPolicy/AssignmentPolicyProductByRuleTableView';
import { exportAsCSV, exportAsExcel, exportAsPNG } from '../common/exportUtils';
import { Button } from '../components/actions/Button';
import SwitchLayoutTable from '../components/actions/SwitchLayoutTable';
import { HeaderAppMenu } from '../components/headers/HeaderAppMenu';
import * as Icon from '../components/icons';
import { Container } from '../components/layout/ContainerFlex';
import { Spacer } from '../components/layout/Spacer';
// import Menu from '../components/menu/Menu';
import Menu from '../components/menu/Menu';
import LayoutActiveFeatureTag from '../layout/LayoutActiveFeatureTag';
import { stageStateById } from '../layout/stage/stage.state';
import NavigationModeToolbar from '../layout/toolbar/NavigationModeToolbar';
import {
  viewerAutoSizeId,
  viewerSelectedLayout,
} from '../layout/viewer/store/viewer.state';
import OrderLinesByRuleTable from '../pickingPolicy/OrderLinesByRuleTable';
import ResourcePolicyAgentViewContainer from '../resourcePolicy/agent/ResourcePolicyAgentViewContainer';
import OptimisationLayoutView from './OptimisationLayoutView';
import SimulationFilters from './SimulationFilters';
import SimulationForecastView from './SimulationForecastView';
import SimulationLayoutView from './SimulationLayoutView';
import SimulationHelperGettingStarted from './helper/SimulationHelperGettingStarted';
import ReassignJobsView from './stepContentViews/ReassignJobsView';
import SimulationComplianceView from './stepContentViews/SimulationComplianceView';
import SimulationDatePicker from './stepContentViews/SimulationDatePicker';
import SimulationForecastSwitch from './stepContentViews/SimulationForecastSwitch';
import SimulationIssuesView from './stepContentViews/SimulationIssuesView';
import SimulationOptimisationSetupView from './stepContentViews/SimulationOptimisationSetupView';
import SimulationPickingRequirementView from './stepContentViews/SimulationPickingRequirementView';
import { allocationAnalyzeId } from './store/allocation/allocation.state';
import {
  analyzeContentViewAs,
  itemsContentViewAs,
} from './store/analyze.state';
import { assignmentPolicySelectedIdentity } from './store/assignmentPolicy.state';
import {
  optimisationAnalyzeId,
  optimisationStatus,
  optimisationViewMode,
} from './store/optimisation.state';
import { pickingPolicySelectedDetailsIdentityAtom } from './store/pickingPolicy/pickingPolicy.state';
import {
  simulationAnalyzeId,
  simulationAnalyzeResult,
  simulationEffectiveAssignmentId,
  simulationIsEditable,
  simulationWithAllocation,
} from './store/simulation.state';
import { OptimisationViewMode } from './store/simulation.types';
import {
  simulationAnalyseTabKey,
  simulationPolicyTabKey,
  simulationWizardSelectedStep,
} from './store/simulation.wizard.state';
import { workforceSelectedAgentId } from './store/workforce.state';
import SimulationHeatmapToolbar from './toolbars/SimulationHeatmapToolbar';
import SimulationViewModeToolbar from './toolbars/SimulationViewModeToolbar';
import useLayoutAreaWatcher from './watcher/useLayoutAreaWatcher';

// Menu option interface for export functionality
type MenuOption = {
  id: string;
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
};

const SimulationMainContentView: React.FC = () => {
  const { t } = useTranslation('simulation');
  const [assignmentPolicySelection, setAssignmentPolicySelection] =
    useRecoilState(assignmentPolicySelectedIdentity);

  const step = useRecoilValue(simulationWizardSelectedStep);
  const layout = useRecoilValue(viewerSelectedLayout);
  const viewMode = useRecoilValue(optimisationViewMode);
  const canEdit = useRecoilValue(simulationIsEditable);
  const optStatus = useRecoilValue(optimisationStatus);

  const [isColMode, setIsColMode] = React.useState(false);
  const setAutoSizeId = useSetRecoilState(viewerAutoSizeId);
  const [isExporting, setIsExporting] = useState(false);

  const simulationLayoutRef = useRef<HTMLDivElement>(null);
  const optimisationLayoutRef = useRef<HTMLDivElement>(null);

  const [analyzeStageState, setAnalyzeStageState] = useRecoilState(
    stageStateById('simulation-area-view'),
  );
  const [optimizeStageState, setOptimizeStageState] = useRecoilState(
    stageStateById('viewer-area-view'),
  );

  // Add layout watchers
  useLayoutAreaWatcher('simulation-area-view');
  useLayoutAreaWatcher('viewer-area-view');

  const handleLayoutModeChange = () => {
    setIsColMode(!isColMode);
    // Just trigger resize, the watchers will handle the adjustment
    setAutoSizeId(nanoid());
  };

  const [pickingDetails, setPickingDetails] = useRecoilState(
    pickingPolicySelectedDetailsIdentityAtom,
  );
  const [analyzeViewAs, setAnalyzeViewAs] =
    useRecoilState(analyzeContentViewAs);

  const analyzeId = useRecoilValue(simulationAnalyzeId);
  const optimisationAnlId = useRecoilValue(optimisationAnalyzeId);
  const allocationAnlId = useRecoilValue(allocationAnalyzeId);

  const effectiveAssignmentId = useRecoilValue(simulationEffectiveAssignmentId);
  // console.log('effectiveAssignmentId (Selected):', effectiveAssignmentId);

  const analyzeResult = useRecoilValue(simulationAnalyzeResult);
  const effectiveAnalyseId = analyzeResult?.id;
  // console.log('effectiveAnalyseId (Selected):', effectiveAnalyseId);

  const selectedAgentId = useRecoilValue(workforceSelectedAgentId);

  const analyzeTabSelected = useRecoilValue(simulationAnalyseTabKey);
  const policyTabSelected = useRecoilValue(simulationPolicyTabKey);
  const analyzeItemsTabSelected = useRecoilValue(itemsContentViewAs);
  const simulationWithAllocate = useRecoilValue(simulationWithAllocation);

  const showAnalyzedTableSwitch =
    step?.id === 'analyse' || step?.id === 'optimise';

  const showProductItems =
    assignmentPolicySelection?.filterType === 'items' &&
    step?.id === 'policy-storage';

  const showResourcePolicy = step?.id === 'policy-resources';
  const showAllocatePreview =
    (step?.id === 'policy-allocation' || step?.id === 'analyse') &&
    _.isNil(analyzeId) &&
    simulationWithAllocate;

  const layoutTitle = layout?.title || '';
  const isOptimisationStep = step?.id === 'optimise';

  const hasAnalyseLayout =
    step?.id !== 'compliance' &&
    (!isOptimisationStep || viewMode !== OptimisationViewMode.Optimised);

  const hasOptimiseLayout =
    optStatus === OptimizationRunStatus.COMPLETED &&
    isOptimisationStep &&
    (viewMode === OptimisationViewMode.Optimised ||
      viewMode === OptimisationViewMode.Compare);

  const showOptimisationSetup =
    isOptimisationStep &&
    optStatus !== OptimizationRunStatus.COMPLETED &&
    optStatus !== OptimizationRunStatus.STOPPING &&
    optStatus !== OptimizationRunStatus.IN_PROGRESS;

  const showPickingPolicyOrderLinesTable =
    step?.id === 'policy-picking' &&
    (pickingDetails?.showOrderLinesByRule ||
      pickingDetails?.showOrderLinesUnmatched);

  // Export as CSV function
  const handleExportAsCSV = () => {
    try {
      setIsExporting(true);

      // Prepare data for export
      const headers = ['Layout', 'Step', 'Status'];
      const data = [[layoutTitle, step?.id || '', optStatus || '']];

      exportAsCSV({
        fileName: `simulation-${step?.id || 'export'}`,
        title: 'Simulation Export',
        data,
        headers,
        trackingLabel: `Simulation ${step?.id}`,
        onComplete: () => setIsExporting(false),
        onError: () => setIsExporting(false),
      });
    } catch (ex) {
      console.error('Export CSV Error:', ex);
      setIsExporting(false);
    }
  };

  // Export as XLS function
  const handleExportAsExcel = () => {
    try {
      setIsExporting(true);

      // Prepare data for export
      const headers = ['Layout', 'Step', 'Status'];
      const data = [[layoutTitle, step?.id || '', optStatus || '']];

      exportAsExcel({
        fileName: `simulation-${step?.id || 'export'}`,
        title: 'Simulation Export',
        data,
        headers,
        trackingLabel: `Simulation ${step?.id}`,
        onComplete: () => setIsExporting(false),
        onError: () => setIsExporting(false),
      });
    } catch (ex) {
      console.error('Export Excel Error:', ex);
      setIsExporting(false);
    }
  };

  // Export as PNG function
  const handleExportAsPNG = (
    targetRef: React.RefObject<HTMLDivElement>,
    name = 'simulation',
  ) => {
    if (!targetRef.current) {
      console.error('Target element not found');
      return;
    }

    setIsExporting(true);

    exportAsPNG({
      fileName: `${name}-${step?.id || 'export'}`,
      title: `${name} Export`,
      elementRef: targetRef,
      scale: 4,
      trackingLabel: `${name} ${step?.id}`,
      onComplete: () => setIsExporting(false),
      onError: () => setIsExporting(false),
    });
  };

  // Create a unified function to generate export menu options
  const createExportMenuOptions = (
    targetRef: React.RefObject<HTMLDivElement>,
    name: string,
  ): MenuOption[] => {
    return [
      // {
      //   id: `export-csv-${name}`,
      //   label: t`Export as CSV`,
      //   icon: <Icon.Download className="h-5 w-5 fill-current" />,
      //   onClick: handleExportAsCSV,
      // },
      // {
      //   id: `export-xls-${name}`,
      //   label: t`Export as Excel`,
      //   icon: <Icon.CloudDownload className="h-5 w-5 fill-current" />,
      //   onClick: handleExportAsExcel,
      // },
      {
        id: `export-png-${name}`,
        label: isExporting ? t`Exporting...` : t`Export PNG`,
        icon: <Icon.CloudDownload className="h-5 w-5 fill-current" />,
        onClick: () => handleExportAsPNG(targetRef, name),
        disabled: isExporting,
      },
    ];
  };

  // Use the unified function to create menu options
  const simulationMenuOptions = createExportMenuOptions(
    simulationLayoutRef,
    'simulation',
  );
  const optimisationMenuOptions = createExportMenuOptions(
    optimisationLayoutRef,
    'optimisation',
  );

  //
  // ---------------- Handle special content view ----------------
  //

  if (
    step?.id === 'analyse' &&
    analyzeTabSelected === 'tab-analyze-items' &&
    analyzeItemsTabSelected === 'allocate'
  ) {
    return (
      <ReassignJobsView
        title={t`Allocate`}
        analyzeId={allocationAnlId}
        hasJobFilterOptions={false}
      />
    );
  }

  if (
    step?.id === 'policy-allocation' &&
    policyTabSelected === 'tab-policy-allocate'
  ) {
    return <SimulationPickingRequirementView />;
  }

  switch (step?.id) {
    case 'getting-started':
      return (
        <>
          <HeaderAppMenu
            hasControls={false}
            title={t`Getting Started with Warehouse Simulation`}
          />
          <SimulationHelperGettingStarted />
        </>
      );
    case 'data-issues':
    case 'import-orders':
    case 'import-assignments':
    case 'import-layout':
    case 'import-items':
      return <SimulationIssuesView />;

    case 'forecast':
      return <SimulationForecastView />;
    case 'profile-items':
      return <SimulationForecastView />;

    // case 'policy-allocation':
    case 'allocation-requirement':
      return <SimulationPickingRequirementView />;

    case 'policy-allocation':
      return <AllocationPreview />;

    case 'compliance':
      return <SimulationComplianceView analyzeId={effectiveAnalyseId} />;

    case 'optimise':
      if (showOptimisationSetup) {
        return <SimulationOptimisationSetupView />;
      }
      break;

    case 'optimise-reassign':
      return (
        <ReassignJobsView
          title={t`Reassign`}
          analyzeId={optimisationAnlId}
          hasJobFilterOptions={true}
        />
      );
  }

  if (showAllocatePreview) {
    return <AllocationPreview />;
  }

  if (showResourcePolicy) {
    return (
      <>
        <HeaderAppMenu hasControls={false} title={t`Agent details`} />
        <ResourcePolicyAgentViewContainer />
      </>
    );
  }

  if (showProductItems) {
    return (
      <AssignmentPolicyProductByRuleTableView
        panelHeader={{
          title: t`Items`,
          onCloseClick: () => setAssignmentPolicySelection(null),
          closable: true,
        }}
      />
    );
  }

  if (showPickingPolicyOrderLinesTable) {
    return (
      <>
        <HeaderAppMenu
          title={t`Order lines`}
          closable
          onCloseClick={() => setPickingDetails(null)}
        />
        <OrderLinesByRuleTable />
      </>
    );
  }

  //analyze single
  if (hasAnalyseLayout && !hasOptimiseLayout) {
    return (
      <>
        <HeaderAppMenu
          title={
            <>
              <SwitchAnalyzeType />
            </>
          }
          subtitle={isOptimisationStep && t`(Before)`}
        >
          {analyzeViewAs === 'dashboard' && selectedAgentId && (
            <>
              <SimulationDatePicker />
              <SimulationForecastSwitch />
            </>
          )}
          <Spacer flexspace />
          <LayoutActiveFeatureTag />
          <SimulationFilters />
          {showAnalyzedTableSwitch && (
            <SwitchLayoutTable
              selected={analyzeViewAs}
              onChange={v => setAnalyzeViewAs(v)}
            />
          )}
          <Menu options={simulationMenuOptions} position="right" />
        </HeaderAppMenu>
        {(analyzeViewAs === 'layout' || !showAnalyzedTableSwitch) && (
          <Container
            componentName={`SimulationLayoutViewContainer`}
            overflow-hidden
            col
            flex1
            className={classNames('relative', 'simulationLayoutRef')}
            ref={simulationLayoutRef}
            data-component="simulation-container"
          >
            <SimulationLayoutView stepId={step?.id} />
            {step?.id === 'policy-routing' && <NavigationModeToolbar />}
            <SimulationHeatmapToolbar />
          </Container>
        )}

        {analyzeViewAs === 'table' && showAnalyzedTableSwitch && (
          <AnalyzedProductsTable analyzeId={analyzeId} />
        )}
        {analyzeViewAs === 'dashboard' && showAnalyzedTableSwitch && (
          <AnalyzeDashboard analyzeId={analyzeId} />
        )}
      </>
    );
  }

  //-- optimise single
  if (hasOptimiseLayout && !hasAnalyseLayout) {
    return (
      <>
        <HeaderAppMenu title={t`Optimisation`} subtitle={t`(After)`}>
          <Spacer flexspace />
          <LayoutActiveFeatureTag />
          <SimulationFilters />
          {showAnalyzedTableSwitch && (
            <SwitchLayoutTable
              selected={analyzeViewAs}
              onChange={v => setAnalyzeViewAs(v)}
            />
          )}

          <Menu options={optimisationMenuOptions} position="right" />
        </HeaderAppMenu>
        {(analyzeViewAs === 'layout' || !showAnalyzedTableSwitch) && (
          <Container
            col
            overflow-hidden
            componentName={`SimulationLayoutViewContainer`}
            className={classNames('relative flex-1', 'simulationLayoutRef')}
            data-component="simulation-container"
          >
            <div
              ref={optimisationLayoutRef}
              className="simulationLayoutRef h-full w-full"
              data-component="optimisation-container"
            >
              <OptimisationLayoutView />
              <SimulationViewModeToolbar />
              <SimulationHeatmapToolbar />
            </div>
          </Container>
        )}

        {analyzeViewAs === 'table' && showAnalyzedTableSwitch && (
          <AnalyzedProductsTable analyzeId={optimisationAnlId} />
        )}
        {analyzeViewAs === 'dashboard' && showAnalyzedTableSwitch && (
          <AnalyzeDashboard analyzeId={optimisationAnlId} />
        )}
      </>
    );
  }
  // default  - analyze compare
  return (
    <>
      <Container className={classNames('relative !h-auto')}>
        <HeaderAppMenu
          title={
            <>
              <Button
                buttonSize="sm"
                buttonType="secondary"
                hasIconAfter={
                  <Icon.ScreenViewDual
                    className={classNames(
                      'h-4 w-4 fill-current',
                      isColMode ? 'rotate-90' : '',
                    )}
                  />
                }
                onPress={() => handleLayoutModeChange()}
                className="rounded shadow-md"
              />
              <SwitchAnalyzeType />
            </>
          }
          subtitle={t`(Before)`}
          className={classNames(
            'flex-1',
            '!border-app-panel-dark',
            'border-r-4',
          )}
        >
          <Spacer flexspace />
          <SimulationFilters />
          <Menu options={simulationMenuOptions} position="right" />
        </HeaderAppMenu>

        {!isColMode && (
          <HeaderAppMenu
            title={<>{t`Optimisation`}</>}
            subtitle={t`(After)`}
            className={classNames('flex-1')}
          >
            <Spacer flexspace />
            <LayoutActiveFeatureTag />
            <SimulationFilters />

            <Menu options={optimisationMenuOptions} position="right" />
          </HeaderAppMenu>
        )}
      </Container>
      <Container
        col={isColMode}
        overflow
        flex1
        className={classNames('relative', 'border-app-background border-8')}
      >
        <Container
          col
          overflow-hidden
          componentName={`SimulationLayoutViewContainer`}
          className={classNames(
            'relative',
            'flex-1',
            'border-app-panel-dark',
            isColMode ? 'border-b-4' : 'ltr:border-r-4 rtl:border-l-4',
            'simulationLayoutRef',
          )}
          ref={simulationLayoutRef}
          data-testid="simulation-container"
        >
          <SimulationLayoutView stepId={step.id} hideViewModeToolbar={true} />
        </Container>
        {isColMode && (
          <HeaderAppMenu
            title={<>{t`Optimisation`}</>}
            subtitle={t`(After)`}
            className={classNames(
              'border-app-panel-dark ltr:border-l rtl:border-r',
            )}
          >
            <Spacer flexspace />
            <LayoutActiveFeatureTag />
            <SimulationFilters />

            <Menu options={optimisationMenuOptions} position="right" />
          </HeaderAppMenu>
        )}
        <Container
          col
          overflow-hidden
          componentName={`OptimisationLayoutViewContainer`}
          className={classNames(
            'relative',
            'flex-1',
            'border-app-panel-dark ltr:border-l rtl:border-r-4',
            'simulationLayoutRef',
          )}
          ref={optimisationLayoutRef}
          data-component="OptimisationLayoutViewContainer"
        >
          <OptimisationLayoutView />
        </Container>
        <SimulationViewModeToolbar />
        <SimulationHeatmapToolbar />
      </Container>
    </>
  );
};

export default SimulationMainContentView;
